import React, { useState, useEffect } from 'react';
import Table from './components/Table/Table';
import TableSearchBar from '../../../../components/common/Table/TableSearchBar';
import { Button, Offcanvas, Form } from 'react-bootstrap';
import MultiSelectFilter from '../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import CheckboxMultiSelect from '../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import {
  getPaymentReciptsByLocalIdAndDateRange,
  getPendingPaymentReceiptsByUserId
} from '../../../../crud/payments_api/payment_receipts';
import { Receipt } from '../types';
import DateFilter from './components/Filters/DateFilter';
import { getUsersOptions, getPedidosTypeOptions } from './components/RenderFunctions';
import { toast } from 'react-toastify';
import { FaFilter, FaFileExcel } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import CustomDatePicker from '../../../../components/common/CustomDatePicker/CustomDatePicker';
import { exportFilteredReceipts } from './utils/receiptsExcelExport';
import { LocalSession } from '../../../../types/LocalSession';
import { MPSwitch, MPPageTitle } from '../../../../components/ui';


// Extender el tipo Receipt con el campo normalizado de fecha
interface ReceiptWithNormalizedDate extends Receipt {
  _created_at?: string | null;
  user_names_str?: string; // nombres de usuarios concatenados para búsqueda
  _items_str?: string;     // nombres de items concatenados para búsqueda
}

// Helper para extraer nombres de items desde selections
const buildItemsString = (receipt: any): string => {
  if (!Array.isArray(receipt?.selections)) return '';
  
  const itemNames = receipt.selections
    .map((sel: any) => sel?.item_name)
    .filter(Boolean) as string[];
  
  return Array.from(new Set(itemNames)).join(' ');
};

interface ReceiptsDisplayProps {
  lastSession?: LocalSession | null;
}

const ReceiptsDisplay: React.FC<ReceiptsDisplayProps> = ({ lastSession }) => {
  const [receipts, setReceipts] = useState<ReceiptWithNormalizedDate[]>([]);
  const [filteredReceipts, setFilteredReceipts] = useState<ReceiptWithNormalizedDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [pedidosType, setPedidosType] = useState<any[]>([]);
  const [mesaOptions, setMesaOptions] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{value: any, label: string}[]>([]);
  const [selectedPedidosType, setSelectedPedidosType] = useState<{value: any, label: string}[]>([]);
  const [selectedMesas, setSelectedMesas] = useState<{value: any, label: string}[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedProductos, setSelectedProductos] = useState<{value: any, label: string}[]>([]);

  const [showFilters, setShowFilters] = useState(false);
  const [soloSesionActual, setSoloSesionActual] = useState(false);
  
  // Leer parámetro pendingUserId de la URL
  const params = new URLSearchParams(window.location.search);
  const pendingUserId = params.get('pendingUserId');

  // Opciones de estatus
  const statusOptions = [
    { value: 'refunded',     label: 'Reembolsado' },
    { value: 'cancelled',    label: 'Cancelado'   },
    { value: 'paid',         label: 'Pagado'      },  
    { value: 'pending',      label: 'Pendiente' },
    { value: 'insufficient', label: 'Pago Insuficiente' },
  ];

  // Fechas para filtro por rango - por defecto 2 días atrás hasta mañana
  const now = new Date();
  const twoDaysAgo = new Date(now);
  twoDaysAgo.setDate(now.getDate() - 2);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  
  const twoDaysAgoStr = [
    twoDaysAgo.getFullYear(),
    String(twoDaysAgo.getMonth() + 1).padStart(2, '0'),
    String(twoDaysAgo.getDate()).padStart(2, '0')
  ].join('-');
  
  const tomorrowStr = [
    tomorrow.getFullYear(),
    String(tomorrow.getMonth() + 1).padStart(2, '0'),
    String(tomorrow.getDate()).padStart(2, '0')
  ].join('-');
  
  const [startDate, setStartDate] = useState(twoDaysAgoStr);
  const [endDate, setEndDate] = useState(tomorrowStr);

  // Carga inicial de recibos
  useEffect(() => {
    const fetchReceipts = async () => {

      setLoading(true);
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;
      try {
        let response;
        if (pendingUserId) {
          response = await getPendingPaymentReceiptsByUserId(Number(pendingUserId));
        } else {
          response = await getPaymentReciptsByLocalIdAndDateRange(localId, startDate, endDate);
        }

        const data: ReceiptWithNormalizedDate[] = response.data.map((r: Receipt) => ({
          ...r,
          user_names_str: Array.isArray(r.user_names)
            ? r.user_names.join(' ')
            : r.user_names,
          // Normalizar fecha del recibo a un campo interno (_created_at) usando fallbacks
          _created_at: r.local_timestamp ?? r.timestamp ?? null,
          _items_str: buildItemsString(r)
        }));

        // No filtrar aquí por sesión - se hace en el useEffect de filtrado
        setReceipts(data);
        // filteredReceipts se actualizará mediante el useEffect de filtrado
      } catch (err) {
        console.error('Error loading receipts:', err);
        toast.error('Error al cargar los recibos');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipts();
  }, [soloSesionActual, startDate, endDate, pendingUserId, lastSession]);

  // Carga de usuarios, tipos de pedido, mesas y sesión activa
  useEffect(() => {
    const fetchData = async () => {
      const fetchedUsers = await getUsersOptions();
      const fetchedPedidosType = await getPedidosTypeOptions();
      setUsers(fetchedUsers);
      setPedidosType(fetchedPedidosType);

      // Generar opciones de mesa a partir de los recibos actuales
      // (esto se puede mejorar si hay un endpoint específico de mesas)
      setMesaOptions(
        Array.from(
          new Set(receipts.map(r => r.mesa_id && r.mesa_num ? `${r.mesa_id}|${r.mesa_num}` : null).filter(Boolean))
        )
        .map(str => {
          const [id, num] = String(str).split('|');
          return { value: Number(id), label: num };
        })
      );
    };

    fetchData();
  }, [receipts]);

  // Extraer opciones únicas de productos desde el campo selections de los recibos
  const getProductosOptions = () => {
    const productosSet = new Set<string>();
    receipts.forEach(receipt => {
      if (receipt.selections && Array.isArray(receipt.selections)) {
        receipt.selections.forEach((selection: any) => {
          if (selection.item_name) {
            productosSet.add(selection.item_name);
          }
        });
      }
    });
    return Array.from(productosSet)
      .sort()
      .map(producto => ({ value: producto, label: producto }));
  };

  // Helper de depuración manual (sin logs automáticos). Ejecutar en consola: window.__debugSessionReceipts()
  useEffect(() => {
    (window as any).__debugSessionReceipts = () => {
      console.log('=== DEBUG RECIBOS Y SESIÓN ===');
      console.log('Sesión actual:', lastSession);
      console.log('Filtro activo:', soloSesionActual);
      
      const counts: Record<string, number> = {};
      receipts.forEach(r => {
        const sid = String(r.session_id ?? 'none');
        counts[sid] = (counts[sid] || 0) + 1;
      });
      
      console.log('Recibos por session_id:', counts);
      console.log('Total recibos:', receipts.length);
      console.log('Recibos filtrados:', filteredReceipts.length);
      
      return {
        lastSession,
        soloSesionActual,
        receiptsBySessions: counts,
        totalReceipts: receipts.length,
        filteredReceipts: filteredReceipts.length
      };
    };
    return () => { delete (window as any).__debugSessionReceipts; };
  }, [receipts, filteredReceipts, lastSession, soloSesionActual]);

  // Filtrado local: usuarios, tipos de pedido, mesas, estatus y sesión
  useEffect(() => {
    setLoading(true);
    let filtered = receipts;

    // Filtro de sesión actual (se aplica primero)
    if (soloSesionActual) {
      if (lastSession?.local_session_id) {
        filtered = filtered.filter(r => r.session_id === lastSession.local_session_id);
      } else {
        // Si no hay sesión pero el usuario marcó "Solo sesión actual", no mostrar nada
        filtered = [];
      }
    }

    if (selectedUsers.length > 0) {
      const ids = selectedUsers.map(u => u.value);
      filtered = filtered.filter(r => ids.includes(r.user_id));
    }

    if (selectedPedidosType.length > 0) {
      const ids = selectedPedidosType.map(p => p.value);
      filtered = filtered.filter(r => ids.includes(r.pedido_type_id!));
    }

    if (selectedMesas.length > 0) {
      const ids = selectedMesas.map(m => m.value);
      filtered = filtered.filter(r => ids.includes(r.mesa_id));
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(r => {
        let st: string;
        if (r.is_refunded)        st = 'refunded';
        else if (r.is_cancelled)  st = 'cancelled';
        else if (r.payed_full)    st = 'paid';
        else if (r.active)        st = 'pending';
        else                       st = 'insufficient';
        return selectedStatuses.includes(st);
      });
    }

    // Filtro de productos: verifica si alguna selection tiene un item_name en selectedProductos
    if (selectedProductos.length > 0) {
      filtered = filtered.filter(r => {
        if (!r.selections || !Array.isArray(r.selections)) return false;
        return r.selections.some((sel: any) =>
          selectedProductos.some(prod => prod.value === sel.item_name)
        );
      });
    }

    setTimeout(() => {
      setFilteredReceipts(filtered);
      setLoading(false);
    }, 300);
  }, [
    receipts,
    selectedUsers,
    selectedPedidosType,
    selectedMesas,
    selectedStatuses,
    selectedProductos,
    soloSesionActual,
    lastSession
  ]);

  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
  };
  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    if (!filteredReceipts || filteredReceipts.length === 0) {
      alert('No hay recibos para exportar');
      return;
    }

    try {
      exportFilteredReceipts(filteredReceipts, startDate, endDate);
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      console.error('Error exporting receipts to Excel:', error);
      toast.error('Error al exportar a Excel. Por favor, intenta nuevamente.');
    }
  };

  // Count active filters for mobile badge
  const activeFilterCount = [
    selectedUsers.length > 0,
    selectedPedidosType.length > 0,
    selectedMesas.length > 0,
    selectedStatuses.length > 0,
    selectedProductos.length > 0,
    soloSesionActual,
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-4 p-3 pb-6 lg:gap-5 lg:p-4 lg:pb-8">
      {/* Page Header */}
      <MPPageTitle><span className="text-xl lg:text-2xl">Recibos</span></MPPageTitle>

      {/* Filter Bar — Premium SaaS toolbar */}
      <div className="mp-toolbar">
        {/* Row 1: Search + Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 lg:gap-3">
          {/* Search Input */}
          <div className="flex-1 min-w-[180px] lg:min-w-[220px] max-w-[380px]">
            <TableSearchBar
              data={receipts}
              updateFilteredData={setFilteredReceipts}
              searchFields={['client_name', 'receipt_number', 'user_names_str', 'mesa_num', '_items_str']}
              placeholder="Buscar recibos..."
            />
          </div>

          {/* Desktop: Date pickers + Session toggle */}
          <div className="hidden lg:flex items-center gap-2">
            <div className="min-w-[145px]">
              <CustomDatePicker
                value={startDate}
                onChange={handleStartDateChange}
                disabled={soloSesionActual}
                placeholder="Fecha inicio"
              />
            </div>
            <div className="min-w-[145px]">
              <CustomDatePicker
                value={endDate}
                onChange={handleEndDateChange}
                disabled={soloSesionActual}
                placeholder="Fecha fin"
              />
            </div>
            <MPSwitch
              checked={soloSesionActual}
              onChange={setSoloSesionActual}
              label="Sesión actual"
              size="sm"
              id="current-session-toggle"
            />
          </div>

          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setShowFilters(true)}
            className="lg:hidden mp-toolbar-btn relative"
            aria-label="Abrir filtros"
          >
            <FaFilter className="text-xs" style={{ color: '#FC7556' }} />
            <span>Filtros</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4.5 h-4.5 rounded-full text-[10px] font-bold text-white" style={{ background: '#FC7556', minWidth: '18px', height: '18px' }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Mobile Export — icon-only button */}
          <button
            className="lg:hidden mp-btn-export !px-2.5 !gap-0"
            onClick={handleExportToExcel}
            disabled={loading || filteredReceipts.length === 0}
            title="Exportar a Excel"
            aria-label="Exportar a Excel"
          >
            <FaFileExcel className="text-sm" />
          </button>

          <div className="hidden lg:block ml-auto">
            <button
              className="mp-btn-export"
              onClick={handleExportToExcel}
              disabled={loading || filteredReceipts.length === 0}
              title="Exportar recibos a Excel"
            >
              <FaFileExcel />
              <span>Exportar</span>
            </button>
          </div>
        </div>

        {/* Row 2: Dropdown filters — desktop only */}
        <div className="hidden lg:flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <span className="text-xs font-semibold uppercase tracking-wider opacity-35 mr-1" style={{ color: 'var(--text-color)' }}>Filtrar:</span>
          <div className="min-w-[140px]">
            <CheckboxMultiSelect
              options={users
                .filter(u => u.name !== 'Ninguno')
                .map(u => ({ value: u.id, label: u.name }))}
              value={selectedUsers}
              onChange={setSelectedUsers}
              placeholder="Usuarios"
            />
          </div>
          <div className="min-w-[120px]">
            <CheckboxMultiSelect
              options={pedidosType
                .filter(p => p.name !== 'Ninguno' && p.name !== 'Autoservicio')
                .map(p => ({ value: p.id, label: p.name }))}
              value={selectedPedidosType}
              onChange={setSelectedPedidosType}
              placeholder="Tipo"
            />
          </div>
          <div className="min-w-[110px]">
            <CheckboxMultiSelect
              options={mesaOptions}
              value={selectedMesas}
              onChange={setSelectedMesas}
              placeholder="Mesa"
            />
          </div>
          <div className="min-w-[120px]">
            <CheckboxMultiSelect
              options={statusOptions}
              value={selectedStatuses.map(s => ({ value: s, label: statusOptions.find(opt => opt.value === s)?.label || s }))}
              onChange={(selected) => setSelectedStatuses(selected.map(s => s.value))}
              placeholder="Estado"
            />
          </div>
          <div className="min-w-[130px]">
            <CheckboxMultiSelect
              options={getProductosOptions()}
              value={selectedProductos}
              onChange={setSelectedProductos}
              placeholder="Productos"
            />
          </div>
        </div>
      </div>

      {/* Offcanvas para móvil */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        className="mp-offcanvas receipts-offcanvas"
        backdrop={true}
        scroll={false}
      >
        <Offcanvas.Header className="offcanvas-header">
          <div className="header-content">
            <Offcanvas.Title className="offcanvas-title">
              Opciones de Filtrado
            </Offcanvas.Title>
            <div className="section-indicator">
              Recibos
            </div>
          </div>
          <Button
            variant="none"
            className="close-button"
            onClick={() => setShowFilters(false)}
          >
            <X size={24} />
          </Button>
        </Offcanvas.Header>

        <Offcanvas.Body className="offcanvas-body">
          <div className="filters-container">
            <div className="filter-section">
              <h6 className="filter-section-title">Filtros Generales</h6>
              <div className="filter-group">
                <Form.Label className="filter-label">Rango de fechas</Form.Label>
                <div className="receipt-date-filter-container">
                  <DateFilter
                    startDate={startDate}
                    setStartDate={handleStartDateChange}
                    endDate={endDate}
                    setEndDate={handleEndDateChange}
                  />
                </div>
              </div>
              <div className="filter-group">
                <label className="filter-label">Usuarios</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={users
                      .filter(u => u.name !== 'Ninguno')
                      .map(u => ({ value: u.id, label: u.name }))}
                    onChange={setSelectedUsers}
                    placeholder="Filtrar por Usuarios"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Tipo de Pedido</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={pedidosType
                      .filter(p => p.name !== 'Ninguno' && p.name !== 'Autoservicio')
                      .map(p => ({ value: p.id, label: p.name }))}
                    onChange={setSelectedPedidosType}
                    placeholder="Filtrar por Tipo de Pedido"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Mesa</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={mesaOptions}
                    onChange={setSelectedMesas}
                    placeholder="Filtrar por Mesa"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Estatus</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={statusOptions}
                    onChange={(opts) => setSelectedStatuses(opts.map(o => o.value))}
                    placeholder="Filtrar por Estatus"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Productos</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={getProductosOptions()}
                    onChange={setSelectedProductos}
                    placeholder="Filtrar por Productos"
                  />
                </div>
              </div>
              <div className="filter-group">
                <MPSwitch
                  checked={soloSesionActual}
                  onChange={setSoloSesionActual}
                  label="Solo sesión actual"
                  id="soloSesionActualMobile"
                />
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Content */}
      {loading ? (
        <LoadingSpinner text="Cargando recibos" transparent />
      ) : filteredReceipts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center mp-table-card">
          <p className="text-base opacity-60" style={{ color: 'var(--text-color)' }}>
            ¡No se han registrado recibos hoy!<br />
            Para visualizar anteriores utilice los filtros
          </p>
        </div>
      ) : (
        <Table data={filteredReceipts} setReceipts={setReceipts} />
      )}
    </div>
  );
};

export default ReceiptsDisplay;
